import { useTranslate } from '@ur-solutions/react-hooks'
import { PERMISSIONS } from '@/utils/permissions'
import type { PermissionDomain } from './types'

export function usePermissionDomainTranslations() {
  return useTranslate({
    absence: 'common.absence',
    approve: 'permissions.approve',
    checkInOut: 'permissions.check-in-out',
    company: 'common.company',
    companySettings: 'settings.company-settings',
    driverCard: 'users.driver-card',
    driverdataAll: 'user-types.everyones-driver-activities',
    driverdataSelf: 'user-types.driver-activities-self',
    dropdowns: 'common.dropdowns',
    exports: 'exports.export-data',
    formcreator: 'common.formcreator',
    formResponses: 'common.form-responses',
    forum: 'common.forum',
    forumChangeSticky: 'permissions.change_sticky',
    forumPosts: 'common.forum-posts',
    forumSendEmail: 'permissions.forum-send-email',
    forumSendSms: 'permissions.forum-send-sms',
    forumViewOthersReadStatistics:
      'permissions.forum-view-others-read-statistics',
    forumThreads: 'common.forum-threads',
    events: 'events.events',
    general: 'permissions.company-settings-general',
    handbook: 'common.handbook',
    infringements: 'common.infringements',
    issues: 'common.issues',
    routePlanner: 'common.route-planner',
    sms: 'common.sms',
    tableSettings: 'settings.tables',
    terminal: 'common.terminal',
    timesheets: 'common.hourlist',
    userDocuments: 'common.documents',
    userDocumentsTitle: 'common.user-documents',
    userEquipment: 'common.user-equipment',
    users: 'common.users',
    userChild: 'common.user-child',
    userTypes: 'common.user-types',
    vehicles: 'vehicles.fleet-overview',
    vehiclesNotes: 'vehicles.note-permissions',
    vehiclesDocuments: 'vehicles.vehicle-documents',
    vehiclesExpirations: 'vehicles.vehicle-expirations',
    fleet: 'vehicles.fleet',
    workPeriods: 'salary.work-periods',

    viewAbsencePeriodReport: 'permissions.view-absence-period-report',
    viewAbsencePeriodReportOther:
      'permissions.view-absence-period-report-other',
    manageOwnAbsenceBalance: 'permissions.manage-own-absence-balance',
    manageOthersAbsenceBalance: 'permissions.manage-others-absence-balance',
    manageAbsenceLocks: 'permissions.manage-absence-locks',

    view: 'permissions.view',
    add: 'permissions.add',
    change: 'permissions.change',
    remove: 'permissions.remove',

    viewChange: 'permissions.view-and-change',
    overrideSalaryCode: 'permissions.override-salary-code',
    overrideDepartmentFacility: 'permissions.override-department-facility',
    salary: 'salary.salary-bases',
    salaryAddons: 'salary.time-addons',
    salaryOverrides: 'salary.salary-code-overrides',
    dayAddons: 'salary.fixed-addons',

    viewOwn: 'permissions.view-own',
    addOwn: 'permissions.add-own',
    changeOwn: 'permissions.change-own',
    removeOwn: 'permissions.remove-own',

    viewOthers: 'permissions.view-others',
    addOthers: 'permissions.add-others',
    changeOthers: 'permissions.change-others',
    removeOthers: 'permissions.remove-others',

    viewSalaryMode: 'permissions.view-salary-mode',
    changeSalaryMode: 'permissions.change-salary-mode',

    changeActivityPaidStatus: 'permissions.change-activity-paid-status',
    viewDriverWorkAnalytics: 'permissions.view-driver-work-analytics',
    diet: 'common.diet',

    planner: {
      planner: 'common.planner',
      access: 'permissions.planner-access',
      viewUnownedItems: 'permissions.planner-view-unowned-items',
      manageOwnedItems: 'permissions.planner-manage-owned-items',
      manageUnownedItems: 'permissions.planner-manage-unowned-items',
      useTemplates: 'permissions.planner-use-templates',
      createTemplates: 'permissions.planner-create-templates',
      deleteTemplates: 'permissions.planner-delete-templates',
    },
    descriptions: {
      companyView: 'permissions.descriptions.company-view',
      companyExports: 'permissions.descriptions.company-exports',
      usersView: 'permissions.descriptions.users-view',
      usersAdd: 'permissions.descriptions.users-add',
      usersChange: 'permissions.descriptions.users-change',
      usersRemove: 'permissions.descriptions.users-remove',
      userDocumentsView: 'permissions.descriptions.user-documents-view',
      userDocumentsViewOthers:
        'permissions.descriptions.user-documents-view-others',
      userDocumentsAdd: 'permissions.descriptions.user-documents-add',
      userDocumentsChange: 'permissions.descriptions.user-documents-change',
      userDocumentsRemove: 'permissions.descriptions.user-documents-remove',
      userEquipmentView: 'permissions.descriptions.user-equipment-view',
      userEquipmentAdd: 'permissions.descriptions.user-equipment-add',
      userEquipmentChange: 'permissions.descriptions.user-equipment-change',
      userEquipmentRemove: 'permissions.descriptions.user-equipment-remove',
      userChildViewOwn: 'permissions.descriptions.user-child-view-own',
      userChildAddOwn: 'permissions.descriptions.user-child-add-own',
      userChildChangeOwn: 'permissions.descriptions.user-child-change-own',
      userChildRemoveOwn: 'permissions.descriptions.user-child-remove-own',
      userChildViewOthers: 'permissions.descriptions.user-child-view-others',
      userChildAddOthers: 'permissions.descriptions.user-child-add-others',
      userChildChangeOthers:
        'permissions.descriptions.user-child-change-others',
      userChildRemoveOthers:
        'permissions.descriptions.user-child-remove-others',
      forumSendSms: 'permissions.descriptions.forum-send-sms',
      forumSendEmail: 'permissions.descriptions.forum-send-email',
      forumSticky: 'permissions.descriptions.forum-sticky',
      forumViewOthersReadStatistics:
        'permissions.descriptions.forum-view-others-read-statistics',
      forumThreadsView: 'permissions.descriptions.forum-threads-view',
      forumThreadsAdd: 'permissions.descriptions.forum-threads-add',
      forumThreadsChange: 'permissions.descriptions.forum-threads-change',
      forumPostsView: 'permissions.descriptions.forum-posts-view',
      forumPostsAdd: 'permissions.descriptions.forum-posts-add',
      forumPostsChange: 'permissions.descriptions.forum-posts-change',
      eventsAdd: 'permissions.descriptions.events-add',
      eventsChange: 'permissions.descriptions.events-change',
      driverCardView: 'permissions.descriptions.driver-card-view',
      driverCardAdd: 'permissions.descriptions.driver-card-add',
      activitiesOwnView: 'permissions.descriptions.activities-own-view',
      activitiesOwnApprove: 'permissions.descriptions.activities-own-approve',
      dietChange: 'permissions.descriptions.diet-change',
      activitiesAllViewChange:
        'permissions.descriptions.activities-all-view-change',
      activitiesAllApprove: 'permissions.descriptions.activities-all-approve',
      activitiesAllPaidStatus:
        'permissions.descriptions.activities-all-paid-status',
      activitiesAllWorkAnalytics:
        'permissions.descriptions.activities-all-work-analytics',
      terminalView: 'permissions.descriptions.terminal-view',
      terminalAdd: 'permissions.descriptions.terminal-add',
      terminalChange: 'permissions.descriptions.terminal-change',
      terminalRemove: 'permissions.descriptions.terminal-remove',
      infringementsView: 'permissions.descriptions.infringements-view',
      infringementsViewOthers:
        'permissions.descriptions.infringements-view-others',
      infringementsAdd: 'permissions.descriptions.infringements-add',
      infringementsChange: 'permissions.descriptions.infringements-change',
      infringementsRemove: 'permissions.descriptions.infringements-remove',
      issuesView: 'permissions.descriptions.issues-view',
      issuesViewOthers: 'permissions.descriptions.issues-view-others',
      issuesAdd: 'permissions.descriptions.issues-add',
      issuesChange: 'permissions.descriptions.issues-change',
      issuesRemove: 'permissions.descriptions.issues-remove',
      timesheetsView: 'permissions.descriptions.timesheets-view',
      timesheetsAdd: 'permissions.descriptions.timesheets-add',
      timesheetsChange: 'permissions.descriptions.timesheets-change',
      timesheetsRemove: 'permissions.descriptions.timesheets-remove',
      timesheetsViewOthers: 'permissions.descriptions.timesheets-view-others',
      timesheetsOverrideSalaryCode:
        'permissions.descriptions.timesheets-override-salary-code',
      timesheetsOverrideDepartmentFacility:
        'permissions.descriptions.timesheets-override-department-facility',
      timesheetsCheckInOut: 'permissions.descriptions.timesheets-check-in-out',
      absenceView: 'permissions.descriptions.absence-view',
      absenceAdd: 'permissions.descriptions.absence-add',
      absenceChange: 'permissions.descriptions.absence-change',
      absenceRemove: 'permissions.descriptions.absence-remove',
      absenceViewOthers: 'permissions.descriptions.absence-view-others',
      absenceAddOthers: 'permissions.descriptions.absence-add-others',
      absenceChangeOthers: 'permissions.descriptions.absence-change-others',
      absenceRemoveOthers: 'permissions.descriptions.absence-remove-others',
      absencePeriodReport: 'permissions.descriptions.absence-period-report',
      absencePeriodReportOthers:
        'permissions.descriptions.absence-period-report-others',
      absenceManageOwnBalance:
        'permissions.descriptions.absence-manage-own-balance',
      absenceManageOthersBalance:
        'permissions.descriptions.absence-manage-others-balance',
      absenceManageLocks: 'permissions.descriptions.absence-manage-locks',
      handbookView: 'permissions.descriptions.handbook-view',
      handbookAdd: 'permissions.descriptions.handbook-add',
      handbookChange: 'permissions.descriptions.handbook-change',
      formcreatorView: 'permissions.descriptions.formcreator-view',
      formcreatorAdd: 'permissions.descriptions.formcreator-add',
      formcreatorChange: 'permissions.descriptions.formcreator-change',
      formcreatorRemove: 'permissions.descriptions.formcreator-remove',
      formResponsesView: 'permissions.descriptions.form-responses-view',
      formResponsesViewOthers:
        'permissions.descriptions.form-responses-view-others',
      vehiclesView: 'permissions.descriptions.vehicles-view',
      vehiclesAdd: 'permissions.descriptions.vehicles-add',
      vehiclesChange: 'permissions.descriptions.vehicles-change',
      vehiclesRemove: 'permissions.descriptions.vehicles-remove',
      vehicleNotesAdd: 'permissions.descriptions.vehicle-notes-add',
      vehicleNotesChange: 'permissions.descriptions.vehicle-notes-change',
      vehicleNotesRemove: 'permissions.descriptions.vehicle-notes-remove',
      vehicleDocumentsView: 'permissions.descriptions.vehicle-documents-view',
      vehicleDocumentsAdd: 'permissions.descriptions.vehicle-documents-add',
      vehicleDocumentsChange:
        'permissions.descriptions.vehicle-documents-change',
      vehicleDocumentsRemove:
        'permissions.descriptions.vehicle-documents-remove',
      vehicleExpirationsView:
        'permissions.descriptions.vehicle-expirations-view',
      vehicleExpirationsAdd: 'permissions.descriptions.vehicle-expirations-add',
      vehicleExpirationsChange:
        'permissions.descriptions.vehicle-expirations-change',
      vehicleExpirationsRemove:
        'permissions.descriptions.vehicle-expirations-remove',
      fleetView: 'permissions.descriptions.fleet-view',
      salaryBasesView: 'permissions.descriptions.salary-bases-view',
      salaryBasesAdd: 'permissions.descriptions.salary-bases-add',
      salaryBasesChange: 'permissions.descriptions.salary-bases-change',
      salaryBasesRemove: 'permissions.descriptions.salary-bases-remove',
      workPeriodsView: 'permissions.descriptions.work-periods-view',
      workPeriodsViewOthers:
        'permissions.descriptions.work-periods-view-others',
      workPeriodsViewSalaryMode:
        'permissions.descriptions.work-periods-view-salary-mode',
      workPeriodsChangeSalaryMode:
        'permissions.descriptions.work-periods-change-salary-mode',
      workPeriodsApprove: 'permissions.descriptions.work-periods-approve',
      timeAddonsView: 'permissions.descriptions.time-addons-view',
      timeAddonsAdd: 'permissions.descriptions.time-addons-add',
      timeAddonsChange: 'permissions.descriptions.time-addons-change',
      timeAddonsRemove: 'permissions.descriptions.time-addons-remove',
      salaryOverridesView: 'permissions.descriptions.salary-overrides-view',
      salaryOverridesAdd: 'permissions.descriptions.salary-overrides-add',
      salaryOverridesChange: 'permissions.descriptions.salary-overrides-change',
      salaryOverridesRemove: 'permissions.descriptions.salary-overrides-remove',
      dayAddonsView: 'permissions.descriptions.day-addons-view',
      dayAddonsAdd: 'permissions.descriptions.day-addons-add',
      dayAddonsChange: 'permissions.descriptions.day-addons-change',
      dayAddonsRemove: 'permissions.descriptions.day-addons-remove',
      routePlannerView: 'permissions.descriptions.route-planner-view',
      routePlannerAdd: 'permissions.descriptions.route-planner-add',
      routePlannerChange: 'permissions.descriptions.route-planner-change',
      routePlannerRemove: 'permissions.descriptions.route-planner-remove',
      plannerAccess: 'permissions.descriptions.planner-access',
      plannerManageOwned: 'permissions.descriptions.planner-manage-owned',
      plannerViewUnowned: 'permissions.descriptions.planner-view-unowned',
      plannerManageUnowned: 'permissions.descriptions.planner-manage-unowned',
      plannerUseTemplates: 'permissions.descriptions.planner-use-templates',
      plannerCreateTemplates:
        'permissions.descriptions.planner-create-templates',
      plannerDeleteTemplates:
        'permissions.descriptions.planner-delete-templates',
      companySettingsView: 'permissions.descriptions.company-settings-view',
      companySettingsChange: 'permissions.descriptions.company-settings-change',
      companySettingsGeneral:
        'permissions.descriptions.company-settings-general',
      companySettingsAbsence:
        'permissions.descriptions.company-settings-absence',
      companySettingsIssues: 'permissions.descriptions.company-settings-issues',
      companySettingsSms: 'permissions.descriptions.company-settings-sms',
      companySettingsTerminal:
        'permissions.descriptions.company-settings-terminal',
      companySettingsUserTypes:
        'permissions.descriptions.company-settings-user-types',
      companySettingsUserDocuments:
        'permissions.descriptions.company-settings-user-documents',
      companySettingsDropdowns:
        'permissions.descriptions.company-settings-dropdowns',
      companySettingsTables: 'permissions.descriptions.company-settings-tables',
    },
  })
}

export function createPermissionDomains(
  translations: ReturnType<typeof usePermissionDomainTranslations>
): PermissionDomain[] {
  const {
    view,
    add,
    change,
    remove,
    viewChange,
    viewOwn,
    addOwn,
    changeOwn,
    removeOwn,
    viewOthers,
    addOthers,
    changeOthers,
    removeOthers,
    approve,
    checkInOut,
    overrideSalaryCode,
    overrideDepartmentFacility,

    viewAbsencePeriodReport,
    viewAbsencePeriodReportOther,
    manageOwnAbsenceBalance,
    manageOthersAbsenceBalance,
    manageAbsenceLocks,

    viewSalaryMode,
    changeSalaryMode,

    descriptions,
  } = translations

  return [
    {
      title: translations.company,
      permissions: {
        [view]: [PERMISSIONS.companies.view.company],
        [translations.exports]: [PERMISSIONS.companies.export.exportReports],
      },
      descriptions: {
        [view]: descriptions.companyView,
        [translations.exports]: descriptions.companyExports,
      },
    },
    {
      title: translations.users,
      permissions: {
        [view]: [PERMISSIONS.users.view.user],
        [add]: [PERMISSIONS.users.add.user],
        [change]: [PERMISSIONS.users.change.user],
        [remove]: [PERMISSIONS.users.delete.user],
      },
      descriptions: {
        [view]: descriptions.usersView,
        [add]: descriptions.usersAdd,
        [change]: descriptions.usersChange,
        [remove]: descriptions.usersRemove,
      },
    },
    {
      title: translations.userDocumentsTitle,
      permissions: {
        [viewOwn]: [PERMISSIONS.users.view.userdocument],
        [viewOthers]: [PERMISSIONS.users.view.othersuserdocument],
        [add]: [PERMISSIONS.users.add.userdocument],
        [change]: [PERMISSIONS.users.change.userdocument],
        [remove]: [PERMISSIONS.users.delete.userdocument],
      },
      descriptions: {
        [viewOwn]: descriptions.userDocumentsView,
        [viewOthers]: descriptions.userDocumentsViewOthers,
        [add]: descriptions.userDocumentsAdd,
        [change]: descriptions.userDocumentsChange,
        [remove]: descriptions.userDocumentsRemove,
      },
    },
    {
      title: translations.userEquipment,
      permissions: {
        [view]: [
          PERMISSIONS.users.view.userequipment,
          PERMISSIONS.users.view.userequipmenttype,
        ],
        [add]: [
          PERMISSIONS.users.add.userequipment,
          PERMISSIONS.users.add.userequipmenttype,
        ],
        [change]: [
          PERMISSIONS.users.change.userequipment,
          PERMISSIONS.users.change.userequipmenttype,
        ],
        [remove]: [
          PERMISSIONS.users.delete.userequipment,
          PERMISSIONS.users.delete.userequipmenttype,
        ],
      },
      descriptions: {
        [view]: descriptions.userEquipmentView,
        [add]: descriptions.userEquipmentAdd,
        [change]: descriptions.userEquipmentChange,
        [remove]: descriptions.userEquipmentRemove,
      },
    },
    {
      title: translations.userChild,
      permissions: {
        [viewOwn]: [PERMISSIONS.users.view.userchild],
        [addOwn]: [PERMISSIONS.users.add.userchild],
        [changeOwn]: [PERMISSIONS.users.change.userchild],
        [removeOwn]: [PERMISSIONS.users.delete.userchild],
        [viewOthers]: [PERMISSIONS.users.view.userchildother],
        [addOthers]: [PERMISSIONS.users.add.userchildother],
        [changeOthers]: [PERMISSIONS.users.change.userchildother],
        [removeOthers]: [PERMISSIONS.users.delete.userchildother],
      },
      descriptions: {
        [viewOwn]: descriptions.userChildViewOwn,
        [addOwn]: descriptions.userChildAddOwn,
        [changeOwn]: descriptions.userChildChangeOwn,
        [removeOwn]: descriptions.userChildRemoveOwn,
        [viewOthers]: descriptions.userChildViewOthers,
        [addOthers]: descriptions.userChildAddOthers,
        [changeOthers]: descriptions.userChildChangeOthers,
        [removeOthers]: descriptions.userChildRemoveOthers,
      },
    },
    {
      title: translations.forum,
      permissions: {
        [translations.forumSendSms]: [PERMISSIONS.forum.send.thread_sms],
        [translations.forumSendEmail]: [PERMISSIONS.forum.send.thread_email],
        [translations.forumChangeSticky]: [PERMISSIONS.forum.change.sticky],
        [translations.forumViewOthersReadStatistics]: [
          PERMISSIONS.forum.view.othersthreaduserinteraction,
        ],
      },
      descriptions: {
        [translations.forumSendSms]: descriptions.forumSendSms,
        [translations.forumSendEmail]: descriptions.forumSendEmail,
        [translations.forumChangeSticky]: descriptions.forumSticky,
        [translations.forumViewOthersReadStatistics]:
          descriptions.forumViewOthersReadStatistics,
      },
    },
    {
      title: translations.forumThreads,
      permissions: {
        [view]: [
          PERMISSIONS.forum.view.thread,
          PERMISSIONS.forum.view.threadattachment,
        ],
        [add]: [
          PERMISSIONS.forum.add.thread,
          PERMISSIONS.forum.add.threadattachment,
        ],
        [change]: [
          PERMISSIONS.forum.change.thread,
          PERMISSIONS.forum.change.threadattachment,
          PERMISSIONS.forum.delete.thread,
        ],
      },
      descriptions: {
        [view]: descriptions.forumThreadsView,
        [add]: descriptions.forumThreadsAdd,
        [change]: descriptions.forumThreadsChange,
      },
    },
    {
      title: translations.forumPosts,
      permissions: {
        [view]: [
          PERMISSIONS.forum.view.post,
          PERMISSIONS.forum.view.postattachment,
        ],
        [add]: [
          PERMISSIONS.forum.add.post,
          PERMISSIONS.forum.add.postattachment,
        ],
        [change]: [
          PERMISSIONS.forum.change.post,
          PERMISSIONS.forum.change.postattachment,

          PERMISSIONS.forum.delete.post,
          PERMISSIONS.forum.delete.postattachment,
        ],
      },
      descriptions: {
        [view]: descriptions.forumPostsView,
        [add]: descriptions.forumPostsAdd,
        [change]: descriptions.forumPostsChange,
      },
    },
    {
      title: translations.events,
      permissions: {
        [add]: [PERMISSIONS.events.add.event, PERMISSIONS.events.view.event],
        [change]: [
          PERMISSIONS.events.change.event,
          PERMISSIONS.events.delete.event,
        ],
      },
      descriptions: {
        [add]: descriptions.eventsAdd,
        [change]: descriptions.eventsChange,
      },
    },
    {
      title: translations.driverCard,
      permissions: {
        [view]: [PERMISSIONS.users.view.driver_card],
        [add]: [PERMISSIONS.users.add.driver_card],
      },
      descriptions: {
        [view]: descriptions.driverCardView,
        [add]: descriptions.driverCardAdd,
      },
    },
    {
      title: translations.driverdataSelf,
      permissions: {
        [view]: [PERMISSIONS.activities.view.driveractivity],
        [approve]: [PERMISSIONS.activities.approve.driveractivity],
      },
      descriptions: {
        [view]: descriptions.activitiesOwnView,
        [approve]: descriptions.activitiesOwnApprove,
      },
    },
    {
      title: translations.diet,
      permissions: {
        [change]: [PERMISSIONS.activities.change.driverdiet],
      },
      descriptions: {
        [change]: descriptions.dietChange,
      },
    },
    {
      title: translations.driverdataAll,
      permissions: {
        [viewChange]: [PERMISSIONS.activities.change.driveractivity],
        [approve]: [PERMISSIONS.activities.approve.othersdriveractivity],
        [translations.changeActivityPaidStatus]: [
          PERMISSIONS.activities.change.driveractivitypaidstatus,
        ],
        [translations.viewDriverWorkAnalytics]: [
          PERMISSIONS.activities.view.driverworkanalytics,
        ],
      },
      descriptions: {
        [viewChange]: descriptions.activitiesAllViewChange,
        [approve]: descriptions.activitiesAllApprove,
        [translations.changeActivityPaidStatus]:
          descriptions.activitiesAllPaidStatus,
        [translations.viewDriverWorkAnalytics]:
          descriptions.activitiesAllWorkAnalytics,
      },
    },
    {
      title: translations.terminal,
      permissions: {
        [view]: [
          PERMISSIONS.terminal.view.terminal,
          PERMISSIONS.timesheets.view.checkinout,
        ],
        [add]: [PERMISSIONS.timesheets.add.checkinout],
        [change]: [PERMISSIONS.timesheets.change.checkinout],
        [remove]: [PERMISSIONS.timesheets.delete.checkinout],
      },
      descriptions: {
        [view]: descriptions.terminalView,
        [add]: descriptions.terminalAdd,
        [change]: descriptions.terminalChange,
        [remove]: descriptions.terminalRemove,
      },
    },
    {
      title: translations.infringements,
      permissions: {
        [view]: [PERMISSIONS.infringements.view.infringement],
        [viewOthers]: [PERMISSIONS.infringements.view.othersinfringement],
        [add]: [PERMISSIONS.infringements.add.infringement],
        [change]: [PERMISSIONS.infringements.change.infringement],
        [remove]: [PERMISSIONS.infringements.delete.infringement],
      },
      descriptions: {
        [view]: descriptions.infringementsView,
        [viewOthers]: descriptions.infringementsViewOthers,
        [add]: descriptions.infringementsAdd,
        [change]: descriptions.infringementsChange,
        [remove]: descriptions.infringementsRemove,
      },
    },
    {
      title: translations.issues,
      permissions: {
        [view]: [PERMISSIONS.issues.view.issue],
        [viewOthers]: [PERMISSIONS.issues.view.othersissue],
        [add]: [PERMISSIONS.issues.add.issue],
        [change]: [PERMISSIONS.issues.change.issue],
        [remove]: [PERMISSIONS.issues.delete.issue],
      },
      descriptions: {
        [view]: descriptions.issuesView,
        [viewOthers]: descriptions.issuesViewOthers,
        [add]: descriptions.issuesAdd,
        [change]: descriptions.issuesChange,
        [remove]: descriptions.issuesRemove,
      },
    },
    {
      title: translations.timesheets,
      permissions: {
        [view]: [PERMISSIONS.timesheets.view.timeentry],
        [add]: [PERMISSIONS.timesheets.add.timeentry],
        [change]: [PERMISSIONS.timesheets.change.timeentry],
        [remove]: [PERMISSIONS.timesheets.delete.timeentry],
        [viewOthers]: [PERMISSIONS.timesheets.view.otherstimeentry],
        [overrideSalaryCode]: [
          PERMISSIONS.timesheets.overrideTimeEntrySalaryCode,
        ],
        [overrideDepartmentFacility]: [
          PERMISSIONS.timesheets.overrideTimeEntryDepartmentFacility,
        ],
        [checkInOut]: [
          PERMISSIONS.timesheets.add.checkinout,
          PERMISSIONS.timesheets.change.checkinout,
        ],
      },
      descriptions: {
        [view]: descriptions.timesheetsView,
        [add]: descriptions.timesheetsAdd,
        [change]: descriptions.timesheetsChange,
        [remove]: descriptions.timesheetsRemove,
        [viewOthers]: descriptions.timesheetsViewOthers,
        [overrideSalaryCode]: descriptions.timesheetsOverrideSalaryCode,
        [overrideDepartmentFacility]:
          descriptions.timesheetsOverrideDepartmentFacility,
        [checkInOut]: descriptions.timesheetsCheckInOut,
      },
    },
    {
      title: translations.absence,
      permissions: {
        [view]: [PERMISSIONS.timesheets.view.absence],
        [add]: [PERMISSIONS.timesheets.add.absence],
        [change]: [PERMISSIONS.timesheets.change.absence],
        [remove]: [PERMISSIONS.timesheets.delete.absence],
        [viewOthers]: [PERMISSIONS.timesheets.view.othersabsence],
        [addOthers]: [PERMISSIONS.timesheets.add.othersabsence],
        [changeOthers]: [PERMISSIONS.timesheets.change.othersabsence],
        [removeOthers]: [PERMISSIONS.timesheets.delete.othersabsence],
        [viewAbsencePeriodReport]: [
          PERMISSIONS.timesheets.view.absenceperiodreport,
        ],
        [viewAbsencePeriodReportOther]: [
          PERMISSIONS.timesheets.view.absenceperiodreportother,
        ],
        [manageOwnAbsenceBalance]: [
          PERMISSIONS.timesheets.add.absencehourtransaction,
          PERMISSIONS.timesheets.change.absencehourtransaction,
          PERMISSIONS.timesheets.delete.absencehourtransaction,
        ],
        [manageOthersAbsenceBalance]: [
          PERMISSIONS.timesheets.add.absencehourtransactionother,
        ],
        [manageAbsenceLocks]: [
          PERMISSIONS.timesheets.view.absencelockconfig,
          PERMISSIONS.timesheets.add.absencelockconfig,
          PERMISSIONS.timesheets.change.absencelockconfig,
          PERMISSIONS.timesheets.delete.absencelockconfig,
        ],
      },
      descriptions: {
        [view]: descriptions.absenceView,
        [add]: descriptions.absenceAdd,
        [change]: descriptions.absenceChange,
        [remove]: descriptions.absenceRemove,
        [viewOthers]: descriptions.absenceViewOthers,
        [addOthers]: descriptions.absenceAddOthers,
        [changeOthers]: descriptions.absenceChangeOthers,
        [removeOthers]: descriptions.absenceRemoveOthers,
        [viewAbsencePeriodReport]: descriptions.absencePeriodReport,
        [viewAbsencePeriodReportOther]: descriptions.absencePeriodReportOthers,
        [manageOwnAbsenceBalance]: descriptions.absenceManageOwnBalance,
        [manageOthersAbsenceBalance]: descriptions.absenceManageOthersBalance,
        [manageAbsenceLocks]: descriptions.absenceManageLocks,
      },
    },
    {
      title: translations.handbook,
      permissions: {
        [view]: [
          PERMISSIONS.handbook.view.manualfolder,
          PERMISSIONS.handbook.view.manualentry,
          PERMISSIONS.handbook.view.manualentryattachment,
        ],
        [add]: [
          PERMISSIONS.handbook.add.manualfolder,
          PERMISSIONS.handbook.add.manualentry,
          PERMISSIONS.handbook.add.manualentryattachment,
        ],
        [change]: [
          PERMISSIONS.handbook.change.manualfolder,
          PERMISSIONS.handbook.change.manualentry,
          PERMISSIONS.handbook.change.manualentryattachment,
        ],
      },
      descriptions: {
        [view]: descriptions.handbookView,
        [add]: descriptions.handbookAdd,
        [change]: descriptions.handbookChange,
      },
    },
    {
      title: translations.formcreator,
      permissions: {
        [view]: [PERMISSIONS.formcreator.view.formtemplate],
        [add]: [
          PERMISSIONS.formcreator.add.filledoutform,
          PERMISSIONS.formcreator.add.formtemplate,
        ],
        [change]: [PERMISSIONS.formcreator.change.formtemplate],
        [remove]: [
          PERMISSIONS.formcreator.delete.filledoutform,
          PERMISSIONS.formcreator.delete.formtemplate,
        ],
      },
      descriptions: {
        [view]: descriptions.formcreatorView,
        [add]: descriptions.formcreatorAdd,
        [change]: descriptions.formcreatorChange,
        [remove]: descriptions.formcreatorRemove,
      },
    },
    {
      title: translations.formResponses,
      permissions: {
        [view]: [PERMISSIONS.formcreator.view.filledoutform],
        [viewOthers]: [PERMISSIONS.formcreator.change.filledoutform],
      },
      descriptions: {
        [view]: descriptions.formResponsesView,
        [viewOthers]: descriptions.formResponsesViewOthers,
      },
    },
    {
      title: translations.vehicles,
      permissions: {
        [view]: [
          PERMISSIONS.vehicles.view.vehicle,
          PERMISSIONS.vehicles.view.vehiclefetchinfo,
          PERMISSIONS.vehicles.view.vehiclenote,
        ],
        [add]: [
          PERMISSIONS.vehicles.add.vehicle,
          PERMISSIONS.vehicles.add.vehiclefetchinfo,
        ],
        [change]: [
          PERMISSIONS.vehicles.change.vehicle,
          PERMISSIONS.vehicles.change.vehiclefetchinfo,
        ],
        [remove]: [
          PERMISSIONS.vehicles.delete.vehicle,
          PERMISSIONS.vehicles.delete.vehiclefetchinfo,
        ],
      },
      descriptions: {
        [view]: descriptions.vehiclesView,
        [add]: descriptions.vehiclesAdd,
        [change]: descriptions.vehiclesChange,
        [remove]: descriptions.vehiclesRemove,
      },
    },
    {
      title: translations.vehiclesNotes,
      permissions: {
        [add]: [PERMISSIONS.vehicles.add.vehiclenote],
        [change]: [PERMISSIONS.vehicles.change.vehiclenote],
        [remove]: [PERMISSIONS.vehicles.delete.vehiclenote],
      },
      descriptions: {
        [add]: descriptions.vehicleNotesAdd,
        [change]: descriptions.vehicleNotesChange,
        [remove]: descriptions.vehicleNotesRemove,
      },
    },
    {
      title: translations.vehiclesDocuments,
      permissions: {
        [view]: [PERMISSIONS.vehicles.view.vehicledocument],
        [add]: [PERMISSIONS.vehicles.add.vehicledocument],
        [change]: [PERMISSIONS.vehicles.change.vehicledocument],
        [remove]: [PERMISSIONS.vehicles.delete.vehicledocument],
      },
      descriptions: {
        [view]: descriptions.vehicleDocumentsView,
        [add]: descriptions.vehicleDocumentsAdd,
        [change]: descriptions.vehicleDocumentsChange,
        [remove]: descriptions.vehicleDocumentsRemove,
      },
    },
    {
      title: translations.vehiclesExpirations,
      permissions: {
        [view]: [PERMISSIONS.vehicles.view.vehicleexpiration],
        [add]: [PERMISSIONS.vehicles.add.vehicleexpiration],
        [change]: [PERMISSIONS.vehicles.change.vehicleexpiration],
        [remove]: [PERMISSIONS.vehicles.delete.vehicleexpiration],
      },
      descriptions: {
        [view]: descriptions.vehicleExpirationsView,
        [add]: descriptions.vehicleExpirationsAdd,
        [change]: descriptions.vehicleExpirationsChange,
        [remove]: descriptions.vehicleExpirationsRemove,
      },
    },
    {
      title: translations.fleet,
      permissions: {
        [view]: [PERMISSIONS.fleet.view.vehiclegroup],
      },
      descriptions: {
        [view]: descriptions.fleetView,
      },
    },
    {
      title: translations.salary,
      permissions: {
        [view]: [
          PERMISSIONS.salary.view.salarybase,
          PERMISSIONS.salary.view.salarybaseentry,
        ],
        [add]: [
          PERMISSIONS.salary.add.salarybase,
          PERMISSIONS.salary.add.salarybaseentry,
        ],
        [change]: [
          PERMISSIONS.salary.change.salarybase,
          PERMISSIONS.salary.change.salarybaseentry,
        ],
        [remove]: [
          PERMISSIONS.salary.delete.salarybase,
          PERMISSIONS.salary.delete.salarybaseentry,
        ],
      },
      descriptions: {
        [view]: descriptions.salaryBasesView,
        [add]: descriptions.salaryBasesAdd,
        [change]: descriptions.salaryBasesChange,
        [remove]: descriptions.salaryBasesRemove,
      },
    },
    {
      title: translations.workPeriods,
      permissions: {
        [view]: [PERMISSIONS.workPeriod.view.workperiod],
        [viewOthers]: [PERMISSIONS.workPeriod.view.othersworkperiod],
        [viewSalaryMode]: [PERMISSIONS.salary.view.salaryMode],
        [changeSalaryMode]: [PERMISSIONS.salary.change.salarySetting],
        [approve]: [
          PERMISSIONS.salary.approve.workperiod,
          PERMISSIONS.workPeriod.approve.workperiod,
          PERMISSIONS.workPeriod.change.workperiod,
        ],
      },
      descriptions: {
        [view]: descriptions.workPeriodsView,
        [viewOthers]: descriptions.workPeriodsViewOthers,
        [viewSalaryMode]: descriptions.workPeriodsViewSalaryMode,
        [changeSalaryMode]: descriptions.workPeriodsChangeSalaryMode,
        [approve]: descriptions.workPeriodsApprove,
      },
    },
    {
      title: translations.salaryAddons,
      permissions: {
        [view]: [PERMISSIONS.salary.view.timeaddon],
        [add]: [PERMISSIONS.salary.add.timeaddon],
        [change]: [PERMISSIONS.salary.change.timeaddon],
        [remove]: [PERMISSIONS.salary.delete.timeaddon],
      },
      descriptions: {
        [view]: descriptions.timeAddonsView,
        [add]: descriptions.timeAddonsAdd,
        [change]: descriptions.timeAddonsChange,
        [remove]: descriptions.timeAddonsRemove,
      },
    },
    {
      title: translations.salaryOverrides,
      permissions: {
        [view]: [PERMISSIONS.salary.view.timeoverride],
        [add]: [PERMISSIONS.salary.add.timeoverride],
        [change]: [PERMISSIONS.salary.change.timeoverride],
        [remove]: [PERMISSIONS.salary.delete.timeoverride],
      },
      descriptions: {
        [view]: descriptions.salaryOverridesView,
        [add]: descriptions.salaryOverridesAdd,
        [change]: descriptions.salaryOverridesChange,
        [remove]: descriptions.salaryOverridesRemove,
      },
    },

    {
      title: translations.dayAddons,
      permissions: {
        [view]: [PERMISSIONS.salary.view.fixedaddon],
        [add]: [PERMISSIONS.salary.add.fixedaddon],
        [change]: [PERMISSIONS.salary.change.fixedaddon],
        [remove]: [PERMISSIONS.salary.delete.fixedaddon],
      },
      descriptions: {
        [view]: descriptions.dayAddonsView,
        [add]: descriptions.dayAddonsAdd,
        [change]: descriptions.dayAddonsChange,
        [remove]: descriptions.dayAddonsRemove,
      },
    },
    {
      title: translations.routePlanner,
      permissions: {
        [view]: [
          PERMISSIONS.routePlanner.view.routePlan,
          PERMISSIONS.routePlanner.view.routePlanPoint,
          PERMISSIONS.routePlanner.view.routePlanInstruction,
          PERMISSIONS.routePlanner.view.routePlanNotice,
          PERMISSIONS.routePlanner.view.routePlanNoticeDetail,
          PERMISSIONS.routePlanner.view.routeRestriction,
          PERMISSIONS.routePlanner.view.routeWeightRestriction,
        ],
        [add]: [
          PERMISSIONS.routePlanner.add.routePlan,
          PERMISSIONS.routePlanner.add.routeRestriction,
          PERMISSIONS.routePlanner.add.routeWeightRestriction,
        ],
        [change]: [PERMISSIONS.routePlanner.change.routePlan],
        [remove]: [PERMISSIONS.routePlanner.delete.routePlan],
      },
      descriptions: {
        [view]: descriptions.routePlannerView,
        [add]: descriptions.routePlannerAdd,
        [change]: descriptions.routePlannerChange,
        [remove]: descriptions.routePlannerRemove,
      },
    },
    {
      title: translations.planner.planner,
      permissions: {
        [translations.planner.access]: [
          PERMISSIONS.planner.view.calendaritem,
          PERMISSIONS.planner.view.calendaritemcomment,
          PERMISSIONS.planner.add.calendaritemcomment,
          PERMISSIONS.planner.change.calendaritemcomment,
        ],
        [translations.planner.manageOwnedItems]: [
          PERMISSIONS.planner.add.calendaritem,
          PERMISSIONS.planner.change.calendaritem,
          PERMISSIONS.planner.delete.calendaritem,
        ],
        [translations.planner.viewUnownedItems]: [
          PERMISSIONS.planner.view.unownedcalendaritem,
        ],
        [translations.planner.manageUnownedItems]: [
          PERMISSIONS.planner.add.unownedcalendaritem,
          PERMISSIONS.planner.change.unownedcalendaritem,
          PERMISSIONS.planner.delete.unownedcalendaritem,
        ],
        [translations.planner.useTemplates]: [
          PERMISSIONS.planner.view.calendarscheduledynamictemplate,
          PERMISSIONS.planner.view.calendarschedulestatictemplate,
        ],
        [translations.planner.createTemplates]: [
          PERMISSIONS.planner.add.calendarscheduledynamictemplate,
          PERMISSIONS.planner.add.calendarschedulestatictemplate,
        ],
        [translations.planner.deleteTemplates]: [
          PERMISSIONS.planner.delete.calendarscheduledynamictemplate,
          PERMISSIONS.planner.delete.calendarschedulestatictemplate,
        ],
      },
      descriptions: {
        [translations.planner.access]: descriptions.plannerAccess,
        [translations.planner.manageOwnedItems]:
          descriptions.plannerManageOwned,
        [translations.planner.viewUnownedItems]:
          descriptions.plannerViewUnowned,
        [translations.planner.manageUnownedItems]:
          descriptions.plannerManageUnowned,
        [translations.planner.useTemplates]: descriptions.plannerUseTemplates,
        [translations.planner.createTemplates]:
          descriptions.plannerCreateTemplates,
        [translations.planner.deleteTemplates]:
          descriptions.plannerDeleteTemplates,
      },
    },
    {
      title: translations.companySettings,
      permissions: {
        [view]: [PERMISSIONS.companies.view.companysetting],
        [change]: [PERMISSIONS.companies.change.companysetting],
        [translations.general]: [
          PERMISSIONS.companies.change.company_general_settings,
        ],
        [translations.absence]: [
          PERMISSIONS.timesheets.view.absencetype,
          PERMISSIONS.timesheets.add.absencetype,
        ],
        [translations.issues]: [
          PERMISSIONS.issues.add.issuecategory,
          PERMISSIONS.issues.change.issuecategory,
          PERMISSIONS.issues.delete.issuecategory,
        ],
        [translations.sms]: [PERMISSIONS.companies.change.company_sms_settings],
        [translations.terminal]: [
          PERMISSIONS.terminal.view.terminal,
          PERMISSIONS.terminal.change.terminal,
        ],
        [translations.userTypes]: [
          PERMISSIONS.users.view.usertype,
          PERMISSIONS.users.add.usertype,
          PERMISSIONS.users.change.usertype,
          PERMISSIONS.users.delete.usertype,
        ],
        [translations.userDocuments]: [
          PERMISSIONS.users.view.userdocument,
          PERMISSIONS.users.add.userdocument,
          PERMISSIONS.users.change.userdocument,
          PERMISSIONS.users.delete.userdocument,
        ],
        [translations.dropdowns]: [
          PERMISSIONS.users.add.dropdown,
          PERMISSIONS.users.change.dropdown,
        ],
        [translations.tableSettings]: [
          PERMISSIONS.dynamic_tables.view.dynamiccolumn,
          PERMISSIONS.dynamic_tables.change.dynamiccolumn,
        ],
      },
      descriptions: {
        [view]: descriptions.companySettingsView,
        [change]: descriptions.companySettingsChange,
        [translations.general]: descriptions.companySettingsGeneral,
        [translations.absence]: descriptions.companySettingsAbsence,
        [translations.issues]: descriptions.companySettingsIssues,
        [translations.sms]: descriptions.companySettingsSms,
        [translations.terminal]: descriptions.companySettingsTerminal,
        [translations.userTypes]: descriptions.companySettingsUserTypes,
        [translations.userDocuments]: descriptions.companySettingsUserDocuments,
        [translations.dropdowns]: descriptions.companySettingsDropdowns,
        [translations.tableSettings]: descriptions.companySettingsTables,
      },
    },
  ]
}
